import React, { useState } from 'react';
import { useMasterEvent } from '../../context/MasterEventContext';
import { competitionService } from '../../services/competition';
import Avatar from './Avatar';
import HelpTip from './HelpTip';
import './TeamsPanel.css';

export default function TeamsPanel({ activityId = null }) {
  const { masterEvent, members, teams, isEventAdmin, refreshData } = useMasterEvent();
  const [name, setName] = useState('');
  const [count, setCount] = useState(2);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const current = teams.filter(t => (t.activityId || null) === activityId);
  const memberId = member => member.user?.id || member.userId;
  const assigned = uid => current.find(t => t.members.includes(uid));
  const command = async (action, payload) => {
    setBusy(true); setError('');
    try { await competitionService.command(masterEvent.id, null, action, { activityId, ...payload }); await refreshData(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const generate = () => {
    if (current.length && !window.confirm('Replace the teams in this scope and randomly reassign members?')) return;
    command('teamGenerate', { teamCount: Number(count), members: members.map(memberId) });
  };
  return <section className="teams-panel">
    {error && <p role="alert" className="mea-error">{error}</p>}
    <p>{activityId ? 'These teams apply to this activity.' : 'These teams are used across the event, including team betting activities.'}<HelpTip label="Teams">{activityId ? 'Activity teams override the event teams for this activity only. Copy event teams to start from the Team Selection rosters.' : 'Every team-based competition uses these teams unless an admin sets up activity-specific teams. A member can be in only one team, and team points add up on the Scores page.'}</HelpTip></p>
    {isEventAdmin && <fieldset disabled={busy} className="teams-controls">
      <form onSubmit={e => { e.preventDefault(); command('teamCreate', { name }).then(() => setName('')); }}>
        <input aria-label="Team name" value={name} onChange={e => setName(e.target.value)} placeholder="New team name" required maxLength={200} />
        <button type="submit">Create team</button>
      </form>
      <div className="teams-actions">
        <label>Teams <input aria-label="Number of teams" type="number" min="2" max="32" value={count} onChange={e => setCount(e.target.value)} /></label>
        <button type="button" onClick={generate}>Assign randomly</button>
        {activityId && <button type="button" onClick={() => command('teamInherit', {})}>Copy event teams</button>}
      </div>
    </fieldset>}
    <div className="teams-grid">
      {current.map(team => <article key={team.id} className="team-roster" style={{ borderTopColor: team.color }}>
        <div className="team-roster-heading"><h4>{team.name}</h4><span>{team.members.length} members</span></div>
        {isEventAdmin && <div className="teams-actions">
          <button disabled={busy} onClick={() => {
            const nextName = window.prompt('Team name', team.name);
            if (nextName?.trim()) command('teamUpdate', { teamId: team.id, name: nextName.trim() });
          }}>Rename</button>
          <button disabled={busy} onClick={() => { if (window.confirm(`Delete ${team.name}?`)) command('teamDelete', { teamId: team.id }); }}>Delete</button>
        </div>}
        <ul>{team.members.map(uid => {
          const member = members.find(m => memberId(m) === uid || m.userId === uid);
          return <li key={uid}><Avatar user={member?.user} size={20} /><span>{member?.user?.displayName || member?.user?.email || uid}</span></li>;
        })}</ul>
        {!team.members.length && <p>No members assigned yet.</p>}
      </article>)}
    </div>
    {!current.length && <p>No teams have been selected yet.</p>}
    <h4>{isEventAdmin ? 'Assign members' : 'Unassigned members'}</h4>
    <div className="teams-members">
      {members.filter(m => isEventAdmin || !assigned(memberId(m))).map(member => {
        const uid = memberId(member);
        const team = assigned(uid);
        return <div className="teams-member" key={member.userId}>
          <Avatar user={member.user} size={22} /><span>{member.user?.displayName || member.userId}</span>
          {isEventAdmin && <select aria-label={`Team for ${member.user?.displayName || member.userId}`} value={team?.id || ''} disabled={busy} onChange={e => {
            const target = e.target.value || team?.id;
            if (target) command('teamAssign', { teamId: target, userId: uid, remove: !e.target.value });
          }}><option value="">Unassigned</option>{current.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select>}
        </div>;
      })}
    </div>
    {!isEventAdmin && <p>An event admin assigns members to teams.</p>}
  </section>;
}
